/**
 * @fileoverview Which stages a My Page target may be moved to from where it is.
 * @description Mirrors the backend's Update Target Stage rules (M2) so the dialog only offers moves
 * the backend will accept: forward to any later stage up to Subscription Requested, to Lost, On Hold
 * or Rejected (with a reason), and, from On Hold, back into the pipeline. Live and New are never
 * offered (Live comes only from Confirm Payment). The backend rechecks every move, so this is
 * guidance, not enforcement.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { MY_PAGE_STAGE_OPTIONS } from '@/features/sales/constants/my-page.constants'

import type { MyPageStage, MyPageStageCode } from '@/features/sales/types/my-page.types'

/** @description The stages a target moves forward through, in pipeline order. */
const FORWARD_ORDER: readonly MyPageStageCode[] = [
  'CONTACTED',
  'DEMO_REQUESTED',
  'PROPOSAL_SENT',
  'UNDER_NEGOTIATION',
  'SUBSCRIPTION_REQUESTED',
]

/** @description The stages a target can be set aside in, or closed at. */
const SIDE_STAGES: readonly MyPageStageCode[] = ['ON_HOLD', 'LOST', 'REJECTED']

/**
 * @description The stages the caller may move a target to from its current stage.
 * @param {MyPageStageCode} current - The target's current stage.
 * @returns {MyPageStage[]} The offered stages with their labels: the later forward stages (any of
 * them when resuming from On Hold), then On Hold, Lost and Rejected except the current one.
 */
export function nextStageOptions(current: MyPageStageCode): MyPageStage[] {
  const position = FORWARD_ORDER.indexOf(current)
  const forward = current === 'ON_HOLD' ? FORWARD_ORDER : FORWARD_ORDER.slice(position + 1)
  const wanted = [...forward, ...SIDE_STAGES.filter((code) => code !== current)]
  return MY_PAGE_STAGE_OPTIONS.filter((option) => wanted.includes(option.code)).sort(
    (a, b) => wanted.indexOf(a.code) - wanted.indexOf(b.code),
  )
}
